import type { FC } from "react";
import { useNavigate } from "react-router";

import type { ContractEntity } from "src/app/features/Contract/types/ContractEntity";
import { ResourceName } from "../../../../core/router/config/ResourceName";
import { withReturnTo } from "../../../../core/router/navigation/returnTo";
import { BASE_PATH, FORM_ACTION } from "../../../../core/router/types/routerTypes";
import type { ExtendedColumnsType } from "../../../index";
import { PropertyTable } from "../../../Table/components/PropertyTable";
import { displayDateTimeValue, displayDateValue } from "../../../Table/components/PropertyTable/transformations/cellTransformations";
import type { AntCustomFormField, CommonFieldAttributes } from "../../types/types";

const getColumnsConfig = (t: Function): ExtendedColumnsType<ContractEntity>[] => {
    return [
        {
            title: t("contract.id"),
            key: "id",
            dataIndex: "id",
        },
        {
            title: t("contract.contractNumber"),
            key: "contractNumber",
            dataIndex: "contractNumber",
        },
        {
            title: t("contract.organizationUnit.name"),
            key: "siteName",
            dataIndex: "siteName",
        },
        {
            title: t("contract.validFrom"),
            key: "validFrom",
            dataIndex: "validFrom",
            render: (value) => displayDateValue(value),
        },
        {
            title: t("contract.validTo"),
            key: "validTo",
            dataIndex: "validTo",
            render: (value) => displayDateValue(value),
        },
        {
            title: t("contract.changed"),
            dataIndex: "changed",
            key: "changed",
            render: (value) => displayDateTimeValue(value),
        },
    ];
};

export interface AcquirersContractsTableProps extends AntCustomFormField<ContractEntity[]>, CommonFieldAttributes {}

export const AcquirersContractsTable: FC<AcquirersContractsTableProps> = ({ value = [], onChange, readOnly }) => {
    const navigate = useNavigate();

    const onDataChanged = (data: ContractEntity[]) => {
        if (onChange) {
            onChange(data);
        }
    };

    const onRowClick = (record: ContractEntity) => {
        const type = record.physical ? "physical" : "virtual";
        const route = `${BASE_PATH}/${ResourceName.CONTRACTS}/${type}/${FORM_ACTION.EDIT}/${
            record.id
        }?discriminator=${type.toUpperCase()}&service=true&id=${record.id}`;
        navigate(route, withReturnTo(location));
    };

    return (
        <PropertyTable<ContractEntity>
            name="acquirersContracts_table"
            getColumnsConfig={getColumnsConfig}
            propertyAsRowKey="id"
            tableData={value || []}
            readOnly={readOnly || false}
            onDataChanged={onDataChanged}
            onRow={(record: ContractEntity) => {
                return {
                    onClick: () => onRowClick(record),
                };
            }}
            rowsAreSelectable={false}
        />
    );
};
